import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  LogOut,
  Truck,
  Loader2,
  RefreshCw,
  CalendarDays,
  Phone,
  MapPin,
  DollarSign,
  Users,
  CheckCircle2,
  Clock,
  UserPlus,
  Trash2,
  Edit,
  Send,
  KeyRound,
} from "lucide-react";
import { authApi, logout, formatApiError } from "@/lib/auth";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  scheduled: "bg-[#fffbf0] text-[#ff6b35] border border-[#ff6b35]/40",
  in_progress: "bg-[#ff6b35] text-white",
  completed: "bg-[#1a1a1a] text-white",
  cancelled: "bg-white text-[#333] border border-[#333]/30",
};

const PAYMENT_COLORS = {
  paid: "bg-[#ff6b35] text-white",
  pending: "bg-[#fffbf0] text-[#333] border border-[#333]/20",
  failed: "bg-white text-[#1a1a1a] border-2 border-[#ff6b35]",
  expired: "bg-white text-[#1a1a1a] border-2 border-[#1a1a1a]/30",
};

const fmtCurrency = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard({ admin }) {
  const navigate = useNavigate();
  const [changePwOpen, setChangePwOpen] = useState(false);
  const onLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#fffbf0]">
      <header className="bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#ff6b35]">
              <Truck size={18} strokeWidth={2.5} />
            </span>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-sm">Come Pick Up My Trash</div>
              <div className="text-white/60 text-[11px] uppercase tracking-widest">Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/today"
              data-testid="todays-run-link"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold px-3 py-1.5 rounded-lg text-sm transition-colors"
            >
              <Truck size={14} /> Today's Run
            </Link>
            <Link
              to="/admin/today"
              data-testid="todays-run-link-mobile"
              className="sm:hidden grid place-items-center w-9 h-9 rounded-lg bg-[#ff6b35] hover:bg-[#ff8c42]"
              aria-label="Today's Run"
            >
              <Truck size={16} />
            </Link>
            <div className="hidden md:block text-right">
              <div className="text-xs text-white/60">Signed in</div>
              <div className="text-sm font-bold">{admin?.email}</div>
            </div>
            <Button
              data-testid="admin-change-password-btn"
              onClick={() => setChangePwOpen(true)}
              variant="ghost"
              className="text-white hover:bg-white/10 hover:text-white"
              title="Change password"
            >
              <KeyRound size={16} className="sm:mr-2" /> <span className="hidden sm:inline">Password</span>
            </Button>
            <Button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              variant="ghost"
              className="text-white hover:bg-white/10 hover:text-white"
            >
              <LogOut size={16} className="sm:mr-2" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-white border-2 border-[#1a1a1a]/10 p-1 h-auto">
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="data-[state=active]:bg-[#ff6b35] data-[state=active]:text-white font-bold">
              <CalendarDays size={14} className="mr-1.5" /> Bookings
            </TabsTrigger>
            <TabsTrigger data-testid="tab-staff" value="staff" className="data-[state=active]:bg-[#ff6b35] data-[state=active]:text-white font-bold">
              <Users size={14} className="mr-1.5" /> Staff
            </TabsTrigger>
            <TabsTrigger data-testid="tab-revenue" value="revenue" className="data-[state=active]:bg-[#ff6b35] data-[state=active]:text-white font-bold">
              <DollarSign size={14} className="mr-1.5" /> Revenue
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <BookingsTab />
          </TabsContent>
          <TabsContent value="staff" className="mt-6">
            <StaffTab />
          </TabsContent>
          <TabsContent value="revenue" className="mt-6">
            <RevenueTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// =================================================================
// BOOKINGS TAB
// =================================================================
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (paymentFilter !== "all") params.payment_status = paymentFilter;
      if (dateFilter) params.date = dateFilter;
      const [b, s] = await Promise.all([
        authApi.get("/admin/bookings", { params }),
        authApi.get("/admin/staff"),
      ]);
      setBookings(b.data.bookings || []);
      setStaff(s.data.staff || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paymentFilter, dateFilter]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5 items-stretch sm:items-end">
        <div className="flex-1 min-w-0">
          <Label className="text-xs font-bold uppercase tracking-widest">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="mt-1 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-xs font-bold uppercase tracking-widest">Payment</Label>
          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger data-testid="filter-payment" className="mt-1 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-xs font-bold uppercase tracking-widest">Pickup date</Label>
          <Input
            data-testid="filter-date"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="mt-1 bg-white"
          />
        </div>
        <Button
          data-testid="bookings-refresh"
          onClick={load}
          variant="outline"
          className="border-2 border-[#1a1a1a]/20 font-bold"
        >
          <RefreshCw size={14} className={loading ? "animate-spin mr-2" : "mr-2"} /> Refresh
        </Button>
        <Button
          data-testid="send-reminders-btn"
          onClick={async () => {
            try {
              const { data } = await authApi.post("/admin/bookings/send-reminders");
              toast.success(`Sent ${data.scheduled_reminders} reminder${data.scheduled_reminders === 1 ? "" : "s"} for ${data.date}.`);
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          variant="outline"
          className="border-2 border-[#ff6b35] text-[#ff6b35] hover:bg-[#fffbf0] font-bold"
        >
          <Send size={14} className="mr-2" /> Send tomorrow's reminders
        </Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Loader2 className="animate-spin text-[#ff6b35]" size={28} />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#1a1a1a]/20 rounded-xl p-10 text-center">
          <div className="text-[#333]">No bookings match the current filters.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <BookingCard
              key={b.id}
              booking={b}
              staff={staff}
              onEdit={() => setEditing(b)}
              onQuickStatus={async (newStatus) => {
                try {
                  await authApi.patch(`/admin/bookings/${b.id}`, { status: newStatus });
                  toast.success(`Marked ${newStatus.replace("_", " ")}`);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
            />
          ))}
        </div>
      )}

      <EditBookingDialog
        booking={editing}
        staff={staff}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </div>
  );
}

function BookingCard({ booking, staff, onEdit, onQuickStatus }) {
  return (
    <div data-testid={`booking-row-${booking.id}`} className="bg-white border-l-[5px] border-[#ff6b35] rounded-r-xl p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-extrabold text-lg text-[#1a1a1a]">{booking.customer_name}</span>
            <Badge className={`${STATUS_COLORS[booking.status] || ""} text-[11px] font-bold uppercase`}>
              {booking.status.replace("_", " ")}
            </Badge>
            <Badge className={`${PAYMENT_COLORS[booking.payment_status] || ""} text-[11px] font-bold uppercase`}>
              {booking.payment_status}
            </Badge>
            <span className="text-xs text-[#333] font-mono">#{booking.order_number}</span>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#333]">
            <div className="flex items-center gap-1.5"><CalendarDays size={13} className="text-[#ff6b35]" /> {booking.pickup_date} · {booking.pickup_time_slot}</div>
            <div className="flex items-center gap-1.5"><Phone size={13} className="text-[#ff6b35]" /> {booking.phone}</div>
            <div className="flex items-center gap-1.5 truncate"><MapPin size={13} className="text-[#ff6b35]" /> {booking.address}, {booking.city} {booking.zip_code}</div>
            <div className="flex items-center gap-1.5"><DollarSign size={13} className="text-[#ff6b35]" /> {fmtCurrency(booking.total_price)} · {booking.tier.replace("_", " ")}</div>
            <div className="flex items-center gap-1.5 sm:col-span-2">
              <Users size={13} className="text-[#ff6b35]" />
              {booking.assigned_staff_name ? (
                <span>Assigned: <span className="font-bold">{booking.assigned_staff_name}</span></span>
              ) : (
                <span className="text-[#333]/60 italic">Unassigned</span>
              )}
            </div>
          </div>
          {booking.notes ? (
            <div className="mt-2 text-xs text-[#333] bg-[#fffbf0] rounded px-2 py-1 border-l-2 border-[#ff6b35]">
              Note: {booking.notes}
            </div>
          ) : null}
        </div>
        <div className="flex flex-row lg:flex-col gap-2 flex-wrap">
          {booking.status === "scheduled" && (
            <Button
              data-testid={`mark-inprogress-${booking.id}`}
              onClick={() => onQuickStatus("in_progress")}
              className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold rounded-lg text-xs h-9"
            >
              <Clock size={14} className="mr-1" /> Start
            </Button>
          )}
          {booking.status === "in_progress" && (
            <Button
              data-testid={`mark-completed-${booking.id}`}
              onClick={() => onQuickStatus("completed")}
              className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 text-white font-bold rounded-lg text-xs h-9"
            >
              <CheckCircle2 size={14} className="mr-1" /> Complete
            </Button>
          )}
          <Button
            data-testid={`edit-booking-${booking.id}`}
            onClick={onEdit}
            variant="outline"
            className="border-2 border-[#1a1a1a]/20 font-bold rounded-lg text-xs h-9"
          >
            <Edit size={14} className="mr-1" /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditBookingDialog({ booking, staff, onClose, onSaved }) {
  const [status, setStatus] = useState("");
  const [staffId, setStaffId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (booking) {
      setStatus(booking.status || "scheduled");
      setStaffId(booking.assigned_staff_id || "");
      setNotes(booking.notes || "");
    }
  }, [booking]);

  if (!booking) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status, notes };
      payload.assigned_staff_id = staffId || "";
      await authApi.patch(`/admin/bookings/${booking.id}`, payload);
      toast.success("Booking updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="edit-booking-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold">Edit booking</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-[#333]">
            <div><span className="font-bold">{booking.customer_name}</span> · {booking.order_number}</div>
            <div>{booking.pickup_date} · {booking.pickup_time_slot}</div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="edit-status" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Assign staff</Label>
            <Select value={staffId || "unassigned"} onValueChange={(v) => setStaffId(v === "unassigned" ? "" : v)}>
              <SelectTrigger data-testid="edit-staff" className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {staff.filter((s) => s.active).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Internal notes</Label>
            <Input
              data-testid="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1"
              placeholder="Gate code, dog on premises, etc."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="save-booking-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold"
          >
            {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================
// STAFF TAB
// =================================================================
function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", role: "crew" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/staff");
      setStaff(data.staff || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone required");
      return;
    }
    setCreating(true);
    try {
      await authApi.post("/admin/staff", form);
      toast.success(`${form.name} added`);
      setForm({ name: "", phone: "", email: "", role: "crew" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await authApi.patch(`/admin/staff/${s.id}`, { active: !s.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? They'll be unassigned from any bookings.`)) return;
    try {
      await authApi.delete(`/admin/staff/${s.id}`);
      toast.success("Staff removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-1">
        <div className="bg-white border-l-[5px] border-[#ff6b35] rounded-r-xl p-5">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#ff6b35]" />
            <h3 className="font-display font-extrabold text-lg">Add crew member</h3>
          </div>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs font-bold">Name</Label>
              <Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jamie Carter" />
            </div>
            <div>
              <Label className="text-xs font-bold">Phone</Label>
              <Input data-testid="staff-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(216) 555-0123" />
            </div>
            <div>
              <Label className="text-xs font-bold">Email (optional)</Label>
              <Input data-testid="staff-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jamie@example.com" />
            </div>
            <div>
              <Label className="text-xs font-bold">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="crew">Crew</SelectItem>
                  <SelectItem value="driver">Driver</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="staff-create-btn"
              onClick={create}
              disabled={creating}
              className="w-full bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold rounded-lg"
            >
              {creating ? <><Loader2 size={14} className="mr-1 animate-spin" /> Adding…</> : "Add to team"}
            </Button>
          </div>
        </div>
      </div>

      <div className="md:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold text-lg">Team ({staff.length})</h3>
          <Button onClick={load} variant="outline" size="sm" className="border-2 border-[#1a1a1a]/20 font-bold">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
        {loading ? (
          <div className="grid place-items-center py-10">
            <Loader2 className="animate-spin text-[#ff6b35]" size={24} />
          </div>
        ) : staff.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-[#1a1a1a]/20 rounded-xl p-8 text-center text-[#333]">
            No crew yet. Add your first team member on the left.
          </div>
        ) : (
          <div className="space-y-3">
            {staff.map((s) => (
              <div key={s.id} data-testid={`staff-row-${s.id}`} className="bg-white rounded-xl p-4 border border-[#1a1a1a]/10 flex items-center gap-3">
                <div className="grid place-items-center w-10 h-10 rounded-full bg-[#ff6b35] text-white font-bold uppercase">
                  {s.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-[#1a1a1a]">{s.name}</span>
                    <Badge className="bg-[#fffbf0] text-[#ff6b35] border border-[#ff6b35]/40 text-[10px] uppercase">{s.role}</Badge>
                    {!s.active && <Badge className="bg-[#1a1a1a]/10 text-[#333] text-[10px] uppercase">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-[#333] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span>{s.phone}</span>
                    {s.email ? <span>· {s.email}</span> : null}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleActive(s)}
                  className="border-2 border-[#1a1a1a]/20 font-bold text-xs"
                >
                  {s.active ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  data-testid={`staff-delete-${s.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => remove(s)}
                  className="border-2 border-[#ff6b35] text-[#ff6b35] hover:bg-[#fffbf0] font-bold"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =================================================================
// REVENUE TAB
// =================================================================
function RevenueTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const cards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Today", value: fmtCurrency(stats.revenue.today), key: "today" },
      { label: "This week", value: fmtCurrency(stats.revenue.week), key: "week" },
      { label: "This month", value: fmtCurrency(stats.revenue.month), key: "month" },
    ];
  }, [stats]);

  if (loading && !stats) {
    return (
      <div className="grid place-items-center py-16">
        <Loader2 className="animate-spin text-[#ff6b35]" size={28} />
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-extrabold text-xl">Revenue</h3>
        <Button onClick={load} variant="outline" size="sm" className="border-2 border-[#1a1a1a]/20 font-bold">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="bg-white rounded-xl p-6 border-[3px] border-[#ff6b35]">
            <div className="text-xs font-bold text-[#ff6b35] uppercase tracking-widest">{c.label}</div>
            <div className="font-display text-4xl font-black text-[#1a1a1a] mt-1">{c.value}</div>
            <div className="text-xs text-[#333] mt-1">Paid bookings only</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-6 border border-[#1a1a1a]/10">
          <h4 className="font-display font-extrabold text-lg mb-3">Bookings by status</h4>
          <div className="space-y-2">
            {Object.entries(stats.bookings.by_status || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-[#333] capitalize">{k.replace("_", " ")}</span>
                <span className="font-bold text-[#1a1a1a]">{v}</span>
              </div>
            ))}
            {Object.keys(stats.bookings.by_status || {}).length === 0 && (
              <div className="text-[#333]/60 text-sm italic">No bookings yet.</div>
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-[#1a1a1a]/10">
          <h4 className="font-display font-extrabold text-lg mb-3">Payments</h4>
          <div className="space-y-2">
            {Object.entries(stats.bookings.by_payment || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-[#333] capitalize">{k}</span>
                <span className="font-bold text-[#1a1a1a]">{v}</span>
              </div>
            ))}
            <div className="pt-2 mt-2 border-t border-[#1a1a1a]/10 flex justify-between text-sm">
              <span className="font-bold text-[#ff6b35] uppercase text-xs tracking-widest">Conversion</span>
              <span className="font-bold">
                {stats.bookings.total > 0
                  ? Math.round((stats.bookings.paid / stats.bookings.total) * 100)
                  : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function ChangePasswordDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrentPw("");
    setNewPw("");
    setConfirmPw("");
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New passwords don't match.");
      return;
    }
    if (newPw === currentPw) {
      toast.error("New password must be different from the current password.");
      return;
    }
    setSaving(true);
    try {
      await authApi.post("/admin/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated. Please sign in again.");
      reset();
      onClose();
      // Force re-login with new password
      logout();
      setTimeout(() => navigate("/admin/login"), 400);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent data-testid="change-password-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold">Change password</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="cpw-current" className="text-xs uppercase tracking-widest text-[#1a1a1a]/60 font-bold">Current password</Label>
            <Input
              id="cpw-current"
              data-testid="cpw-current"
              type="password"
              autoComplete="current-password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="cpw-new" className="text-xs uppercase tracking-widest text-[#1a1a1a]/60 font-bold">New password</Label>
            <Input
              id="cpw-new"
              data-testid="cpw-new"
              type="password"
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1"
            />
            <p className="text-[11px] text-[#1a1a1a]/50 mt-1">At least 8 characters.</p>
          </div>
          <div>
            <Label htmlFor="cpw-confirm" className="text-xs uppercase tracking-widest text-[#1a1a1a]/60 font-bold">Confirm new password</Label>
            <Input
              id="cpw-confirm"
              data-testid="cpw-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={saving} data-testid="cpw-cancel">Cancel</Button>
          <Button
            data-testid="cpw-save"
            onClick={handleSave}
            disabled={saving || !currentPw || !newPw || !confirmPw}
            className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold"
          >
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</> : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
